// T-161 platform calendar (openspec platform-calendar-view D5). The server renders every control as a link, so the calendar
// works without this file; here those links fetch only the calendar fragment (X-DS-Calendar header) and swap it in place, the
// address follows (back/forward work), a phone opens on the agenda, and a tap on a busy day in the month opens that day's sheet.
// Pure helpers are exported for node --test (tools/ds-tests/calendar-view.test.mjs).
import { fetchFragment, remember, type HistoryMode } from './ds/fragment.ts';

/** How the calendar says something: the design system's toast. */
export type CalendarToast = (msg: string, type: string) => void;
/** What ds/index.ts hands the calendar. */
export interface CalendarHost {
  toast?: CalendarToast;
  enhance?: (root: Element) => void;
}

export const HEADER = 'X-DS-Calendar';
export const PHONE = '(max-width: 640px)';

/**
 * A phone opens on the agenda (design D4): the query string to load instead when the address names no view, else null
 * (an address with a view — a shared link, back/forward — is kept as it is).
 */
export function phoneStart(search: string, isPhone: boolean): string | null {
  if (!isPhone) return null;
  const params = new URLSearchParams(search);
  if (params.has('v')) return null;
  params.set('v', 'agenda');
  return '?' + params.toString();
}

/** A plain left click without modifier keys (a ctrl/middle click still opens a new tab). */
function isPlainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

export function initCalendars({ toast, enhance }: CalendarHost): void {
  const first = document.querySelector<HTMLElement>('[data-ds-calview]');
  if (!first) return;
  let running: AbortController | null = null;
  const current = (): HTMLElement | null => document.querySelector<HTMLElement>('[data-ds-calview]');

  async function load(url: string, history: HistoryMode): Promise<void> {
    const cal = current();
    if (!cal) return;
    running?.abort();
    const ctrl = new AbortController();
    running = ctrl;
    cal.setAttribute('aria-busy', 'true');
    try {
      const { element: next } = await fetchFragment(url, HEADER, '1', '[data-ds-calview]', ctrl.signal);
      if (!next) {
        // signed out, a redirect, or an error page: let the browser show what the server answers
        window.location.assign(url);
        return;
      }
      const moreOpen = cal.querySelector<HTMLDetailsElement>('[data-ds-cal-more]')?.open ?? false;
      const more = next.querySelector<HTMLDetailsElement>('[data-ds-cal-more]');
      if (more && moreOpen) more.open = true;
      cal.replaceWith(next);
      enhance?.(next);
      remember(history, { dsCal: true }, url);
    } catch (err) {
      if ((err as Error | null)?.name === 'AbortError') return;
      cal.setAttribute('aria-busy', 'false');
      toast?.('بارگذاری تقویم انجام نشد؛ دوباره تلاش کنید', 'error');
    }
  }

  /** The phone's day sheet: the month cell's events (all of them, also those the cell hides) and a link to the day view. */
  function openSheet(cell: HTMLElement, href: string): boolean {
    const sheet = current()?.querySelector<HTMLDialogElement>('#ds-cal-sheet');
    const chips = cell.querySelectorAll('.ds-cal-chip');
    if (!sheet || chips.length === 0) return false;
    sheet.querySelector('h2')!.textContent = cell.dataset.label ?? '';
    const list = sheet.querySelector('.ds-cal-sheet-list')!;
    list.replaceChildren(
      ...[...chips].map(c => {
        const copy = c.cloneNode(true) as HTMLElement;
        copy.classList.remove('x');
        return copy;
      }),
    );
    sheet.querySelector<HTMLAnchorElement>('[data-ds-cal-sheet-day]')!.href = href;
    sheet.showModal();
    return true;
  }

  // the handlers below start a load and do not wait for it (load reports its own failures)
  /* eslint-disable @typescript-eslint/no-floating-promises */
  document.addEventListener('click', e => {
    if (!isPlainClick(e)) return;
    const target = e.target as Element;
    const day = target.closest<HTMLAnchorElement>('a[data-ds-cal-day]');
    if (day && window.matchMedia(PHONE).matches) {
      const cell = day.closest<HTMLElement>('.ds-cal-cell');
      if (cell && openSheet(cell, day.href)) {
        e.preventDefault();
        return;
      }
    }
    const a = target.closest<HTMLAnchorElement>('a[data-ds-cal-link], a[data-ds-cal-day]');
    if (!a?.closest('[data-ds-calview]')) return;
    e.preventDefault();
    load(a.href, 'push');
  });

  window.addEventListener('popstate', e => {
    if ((e.state as { dsCal?: boolean } | null)?.dsCal) load(window.location.href, null);
  });

  // the first entry is the calendar too, so «back» to it reloads the fragment instead of leaving the address alone
  window.history.replaceState({ dsCal: true }, '', window.location.href);
  const start = phoneStart(window.location.search, window.matchMedia(PHONE).matches);
  if (start) load(window.location.pathname + start, 'replace');
  /* eslint-enable @typescript-eslint/no-floating-promises */
}
